import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthStore } from '@/stores'
import { authApi } from '@/lib/api'
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle as firebaseSignInWithGoogle,
  signInWithFacebook as firebaseSignInWithFacebook,
  signInWithApple as firebaseSignInWithApple,
  signOut as firebaseSignOut,
  resetPassword as firebaseResetPassword,
  getIdToken,
  onAuthChange,
  extractUserInfo,
  type FirebaseUser,
} from '@/lib/firebase'

export function useAuth() {
  const { user, tokens, setUser, setTokens, clearAuth, isLoading, setIsLoading, isHydrated } = useAuthStore()
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Track when a manual auth action is in progress to prevent the auth listener from interfering
  const isAuthActionInProgress = useRef(false)
  // Track if we've already attempted session restoration (persists across hook instances via module scope)
  const hasAttemptedRestore = useRef(false)
  // Track if we've received the initial Firebase auth state
  const hasReceivedInitialAuth = useRef(false)

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (fbUser) => {
      setFirebaseUser(fbUser)

      // Skip if a manual auth action (login/signup) is in progress
      if (isAuthActionInProgress.current) {
        setIsLoading(false)
        return
      }

      // Wait for Zustand to hydrate before making any auth decisions
      const storeState = useAuthStore.getState()
      if (!storeState.isHydrated) {
        // Store not hydrated yet, wait for it
        return
      }

      const currentTokens = storeState.tokens
      const currentUser = storeState.user

      if (fbUser) {
        // Firebase user exists
        if (!currentTokens && !hasAttemptedRestore.current) {
          // We have Firebase auth but no backend tokens - try to restore session
          hasAttemptedRestore.current = true
          try {
            const firebaseToken = await getIdToken(fbUser)
            const response = await authApi.login({ firebase_token: firebaseToken })

            setUser(response.user)
            setTokens({
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
              expiresIn: response.expires_in,
            })
          } catch (err) {
            if (isBackendUserNotFoundError(err)) {
              console.log('Firebase user not registered with backend - signing out')
            } else {
              console.error('Failed to restore session:', err)
            }
            // Clear everything if we can't restore the session
            await firebaseSignOut()
            clearAuth()
          }
        }
        // If we already have tokens, just mark as not loading
        setIsLoading(false)
        hasReceivedInitialAuth.current = true
      } else {
        // Firebase reports no user
        // Only clear auth if we've received the initial auth state before
        // (to avoid clearing during Firebase initialization)
        if (hasReceivedInitialAuth.current) {
          clearAuth()
          hasAttemptedRestore.current = false
        } else if (currentUser && currentTokens) {
          // We have stored auth but Firebase says no user - this is initialization
          // Don't clear yet, wait to see if Firebase reports the user
          hasReceivedInitialAuth.current = true
        } else {
          // No stored auth and no Firebase user - just not logged in
          hasReceivedInitialAuth.current = true
        }
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [setUser, setTokens, clearAuth, setIsLoading])

  // Trigger auth check once store is hydrated
  useEffect(() => {
    if (isHydrated) {
      // If hydrated but no Firebase user listener has fired yet, set loading false
      const timeout = setTimeout(() => {
        if (useAuthStore.getState().isLoading) {
          setIsLoading(false)
        }
      }, 1000) // Give Firebase 1 second to report auth state
      return () => clearTimeout(timeout)
    }
  }, [isHydrated, setIsLoading])

  const signIn = useCallback(
    async (email: string, password: string) => {
      isAuthActionInProgress.current = true
      setIsLoading(true)
      setError(null)

      try {
        // Sign in with Firebase
        const fbUser = await signInWithEmail(email, password)
        const firebaseToken = await getIdToken(fbUser)

        // Exchange for backend tokens
        const response = await authApi.login({ firebase_token: firebaseToken })

        setUser(response.user)
        setTokens({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          expiresIn: response.expires_in,
        })
        hasAttemptedRestore.current = true
        hasReceivedInitialAuth.current = true

        return response.user
      } catch (err) {
        const message = getErrorMessage(err)
        setError(message)
        throw err
      } finally {
        isAuthActionInProgress.current = false
        setIsLoading(false)
      }
    },
    [setIsLoading, setUser, setTokens]
  )

  const signUp = useCallback(
    async (data: {
      email: string
      password: string
      firstName: string
      lastName: string
      phone?: string
      employerCode?: string
    }) => {
      isAuthActionInProgress.current = true
      setIsLoading(true)
      setError(null)

      let fbUser: FirebaseUser | null = null

      try {
        // Create Firebase account
        fbUser = await signUpWithEmail(data.email, data.password)
        const firebaseToken = await getIdToken(fbUser)

        // Register with backend
        const response = await authApi.register({
          firebase_token: firebaseToken,
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone,
          employer_code: data.employerCode,
        })

        setUser(response.user)
        setTokens({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          expiresIn: response.expires_in,
        })
        hasAttemptedRestore.current = true
        hasReceivedInitialAuth.current = true

        return response.user
      } catch (err) {
        // If Firebase account was created but backend registration failed,
        // delete the orphaned Firebase account to prevent future issues
        if (fbUser) {
          try {
            await firebaseSignOut()
          } catch {
            // Ignore sign-out errors
          }
        }

        const message = getErrorMessage(err)
        setError(message)
        throw err
      } finally {
        isAuthActionInProgress.current = false
        setIsLoading(false)
      }
    },
    [setIsLoading, setUser, setTokens]
  )

  // Social login helper - handles the common flow for all social providers
  const handleSocialLogin = useCallback(
    async (signInMethod: () => Promise<FirebaseUser>) => {
      isAuthActionInProgress.current = true
      setIsLoading(true)
      setError(null)

      try {
        const fbUser = await signInMethod()
        const firebaseToken = await getIdToken(fbUser)

        // Try to login first (existing user)
        try {
          const response = await authApi.login({ firebase_token: firebaseToken })
          setUser(response.user)
          setTokens({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            expiresIn: response.expires_in,
          })
          hasAttemptedRestore.current = true
          hasReceivedInitialAuth.current = true
          return response.user
        } catch (loginErr) {
          // If the Firebase user exists but the backend user does not, register
          // the backend account and complete the same session.
          if (!isBackendUserNotFoundError(loginErr)) {
            throw loginErr
          }

          const userInfo = extractUserInfo(fbUser)
          const response = await authApi.register({
            firebase_token: firebaseToken,
            first_name: userInfo.firstName || 'User',
            last_name: userInfo.lastName || '',
          })

          setUser(response.user)
          setTokens({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            expiresIn: response.expires_in,
          })
          hasAttemptedRestore.current = true
          hasReceivedInitialAuth.current = true
          return response.user
        }
      } catch (err) {
        const message = getErrorMessage(err)
        setError(message)
        // Sign out from Firebase if backend registration fails
        await firebaseSignOut()
        throw err
      } finally {
        isAuthActionInProgress.current = false
        setIsLoading(false)
      }
    },
    [setIsLoading, setUser, setTokens]
  )

  const signInWithGoogle = useCallback(async () => {
    return handleSocialLogin(firebaseSignInWithGoogle)
  }, [handleSocialLogin])

  const signInWithFacebook = useCallback(async () => {
    return handleSocialLogin(firebaseSignInWithFacebook)
  }, [handleSocialLogin])

  const signInWithApple = useCallback(async () => {
    return handleSocialLogin(firebaseSignInWithApple)
  }, [handleSocialLogin])

  const signOut = useCallback(async () => {
    setError(null)

    try {
      // Logout from backend first
      if (tokens?.refreshToken) {
        try {
          await authApi.logout({ refresh_token: tokens.refreshToken })
        } catch (err) {
          console.error('Backend logout failed:', err)
        }
      }

      // Sign out from Firebase
      await firebaseSignOut()

      // Clear local state
      clearAuth()
      hasAttemptedRestore.current = false
      // Don't reset hasReceivedInitialAuth - we know we've seen auth state before
    } catch (err) {
      console.error('Sign out error:', err)
      // Clear local state anyway
      clearAuth()
      hasAttemptedRestore.current = false
    }
  }, [tokens, clearAuth])

  const resetPassword = useCallback(async (email: string) => {
    setError(null)

    try {
      await firebaseResetPassword(email)
    } catch (err) {
      const message = getErrorMessage(err)
      setError(message)
      throw err
    }
  }, [])

  const refreshAccessToken = useCallback(async () => {
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await authApi.refresh({ refresh_token: tokens.refreshToken })

      setTokens({
        ...tokens,
        accessToken: response.access_token,
        expiresIn: response.expires_in,
      })

      return response.access_token
    } catch (err) {
      // Refresh token is invalid, sign out
      await signOut()
      throw err
    }
  }, [tokens, setTokens, signOut])

  return {
    user,
    firebaseUser,
    tokens,
    isLoading,
    isAuthenticated: !!user && !!tokens,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signOut,
    resetPassword,
    refreshAccessToken,
    clearError: () => setError(null),
  }
}

function getErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    return err.message || 'Request failed'
  }

  if (err instanceof Error) {
    // Firebase error codes
    const firebaseErrors: Record<string, string> = {
      'auth/email-already-in-use': 'This email is already registered',
      'auth/invalid-email': 'Invalid email address',
      'auth/operation-not-allowed': 'This sign-in method is not enabled',
      'auth/weak-password': 'Password is too weak',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid email or password',
      'auth/too-many-requests': 'Too many attempts. Please try again later',
      'auth/popup-closed-by-user': 'Sign-in was cancelled',
      'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups for this site',
      'auth/account-exists-with-different-credential':
        'An account already exists with this email using a different sign-in method',
      'auth/cancelled-popup-request': 'Sign-in was cancelled',
    }

    // Check for Firebase error code in message
    for (const [code, message] of Object.entries(firebaseErrors)) {
      if (err.message.includes(code)) {
        return message
      }
    }

    return err.message
  }

  return 'An unexpected error occurred'
}

function isApiError(err: unknown): err is { status: number; code?: string; message?: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    typeof (err as { status?: unknown }).status === 'number'
  )
}

function isBackendUserNotFoundError(err: unknown): boolean {
  if (!isApiError(err)) {
    return false
  }

  const message = err.message ?? ''
  return (
    err.status === 404 ||
    (err.status === 400 &&
      (message.includes('User account not found') || message.includes('Please register first')))
  )
}
